import { useState, type KeyboardEvent } from 'react';
import { Eyebrow } from '@/components/ui';
import { X } from 'lucide-react';
import { useTaskStore } from '@/stores/taskStore';
import type { Task } from '@/types';

export function TagsField({ task }: { task: Task }) {
  const updateTask = useTaskStore((s) => s.updateTask);
  const [draft, setDraft] = useState('');

  const onAdd = () => {
    const trimmed = draft.trim().toLowerCase();
    if (!trimmed) return;
    if (task.tags.includes(trimmed)) {
      setDraft('');
      return;
    }
    updateTask(task.id, { tags: [...task.tags, trimmed] });
    setDraft('');
  };

  const onRemove = (tag: string) => {
    updateTask(task.id, { tags: task.tags.filter((t) => t !== tag) });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <Eyebrow className="mb-1">Tags</Eyebrow>
      <div className="flex flex-wrap items-center gap-1">
        {task.tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-line-2 bg-bg px-2 py-0.5 text-2xs text-t2"
          >
            <span className="min-w-0 truncate font-mono">#{tag}</span>
            <button
              type="button"
              onClick={() => onRemove(tag)}
              aria-label={`Remove ${tag}`}
              className="relative -mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-t2 can-hover:hover:bg-rail can-hover:hover:text-t1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset pointer-coarse:before:absolute pointer-coarse:before:-inset-2.5 pointer-coarse:before:content-['']"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add tag…"
          aria-label="Add tag"
          // Tags are lowercase tokens — spellcheck squiggles read as errors.
          spellCheck={false}
          className="min-w-[80px] flex-1 rounded-md bg-transparent text-caption text-t1 outline-none placeholder:text-t3 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        />
      </div>
    </div>
  );
}
